import { ChannelProvider, useAbly, usePresence, usePresenceListener } from 'ably/react';
import Raven from 'raven-js';
import { useEffect } from 'react';
import { useAblyChannel } from './useAblyChannel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const detachChannelTimeoutMap = new Map();
const getTimeoutKey = (ablyId, channelName) => `${ablyId}:${channelName}`;
function attachChannel(ablyId, channel) {
  const timeoutKey = getTimeoutKey(ablyId, channel.name);
  clearTimeout(detachChannelTimeoutMap.get(timeoutKey));
  detachChannelTimeoutMap.delete(timeoutKey);
  channel.attach().catch(err => {
    Raven.captureException(err);
  });
}
function queueDetachChannel(ablyId, channel) {
  const timeoutKey = getTimeoutKey(ablyId, channel.name);
  detachChannelTimeoutMap.set(timeoutKey,
  // Queue the channel to detach after a delay. The reason we delay here is
  // to handle scenarios where the component is unmounted and remounted
  // quickly, such as in React's strict mode. In this case, we want to allow
  // the channel to stay attached until we're sure the component is not going
  // to remount.
  window.setTimeout(() => {
    detachChannelTimeoutMap.delete(timeoutKey);
    channel.detach().catch(err => {
      Raven.captureException(err);
    });
  }, 5000));
}
function ChannelMessageHandler({
  ablyId,
  channelName,
  messageParser,
  onMessage,
  onPlayback,
  skip
}) {
  useAblyChannel({
    ablyId,
    channelName,
    messageParser,
    onMessage,
    onPlayback,
    skip
  });
  return null;
}
ChannelMessageHandler.displayName = 'ChannelMessageHandler';
function ChannelPresenceHandler({
  ablyId,
  channelName,
  onPresence,
  skip
}) {
  const skipPresence = skip || !onPresence;

  // Add current user to the presence set (and remove on unmount)
  usePresence({
    ablyId,
    channelName,
    skip: skipPresence
  });

  // Listen for presence events. `onPresence` callback is omitted here in favor
  // of calling onPresence with the full presence data on any change.
  const {
    presenceData
  } = usePresenceListener({
    ablyId,
    channelName,
    skip: skipPresence
  });
  useEffect(() => onPresence === null || onPresence === void 0 ? void 0 : onPresence(presenceData), [onPresence, presenceData]);
  return null;
}
ChannelPresenceHandler.displayName = 'ChannelPresenceHandler';
export function AblyChannelProvider({
  ablyId,
  channelName,
  channelOptions,
  children,
  messageParser,
  onMessage,
  onPlayback,
  onPresence,
  skip
}) {
  const ablyClient = useAbly(ablyId);

  // Logic to attach/detach the channel on mount/unmount derived from
  // https://github.com/ably/ably-js/issues/1795
  useEffect(() => {
    if (!skip) {
      attachChannel(ablyId, ablyClient.channels.get(channelName, channelOptions));
    }
    return () => {
      if (!skip) {
        queueDetachChannel(ablyId, ablyClient.channels.get(channelName, channelOptions));
      }
    };
  }, [ablyClient, ablyId, channelName, channelOptions, skip]);
  return /*#__PURE__*/_jsxs(ChannelProvider, {
    ablyId: ablyId,
    channelName: channelName,
    options: channelOptions,
    children: [onMessage && /*#__PURE__*/_jsx(ChannelMessageHandler, {
      ablyId: ablyId,
      channelName: channelName,
      messageParser: messageParser,
      onMessage: onMessage,
      onPlayback: onPlayback,
      skip: skip
    }), onPresence && /*#__PURE__*/_jsx(ChannelPresenceHandler, {
      ablyId: ablyId,
      channelName: channelName,
      onPresence: onPresence,
      skip: skip
    }), children]
  });
}
AblyChannelProvider.displayName = 'AblyChannelProvider';